/**
 * Shapes the sales call API returns.
 *
 * Hand-written against the controllers rather than generated, matching
 * `crm-types.ts`. The two stay in step because the API's responses are
 * projections written out field by field in `calls.service.ts`, so a column
 * added there is a compile error here the moment anything reads it.
 */

export type SalesCallStatus = "open" | "closed";
export type SalesCallGroupType = "brand" | "event" | "priority" | "other";
export type SalesScoreTone = "positive" | "neutral" | "negative";

export type SalesAttemptOutcome =
  | "no_answer"
  | "voicemail"
  | "callback_requested"
  | "spoke"
  | "other";

/**
 * What the server decided about a call's staleness, and why.
 *
 * `past_due` covers both a date somebody put on this one call and a group's
 * "we have been at this long enough" rule -- they say the same thing to a
 * reader, which is why they share a name.
 */
export interface Attention {
  overdue: boolean;
  reason: "never_attempted" | "stale" | "past_due" | null;
  hoursOver: number;
  /** Somebody asked to be rung back at a time still to come. */
  waiting: boolean;
  /**
   * When this call is due, ISO 8601, or null when no rule applies.
   *
   * The soonest deadline any rule imposes -- see `attention` on the server.
   * `overdue` flips exactly when this passes, which is what lets one clock
   * render both halves: time left, then time over.
   */
  dueBy: string | null;
}

export interface SalesCall {
  id: number;
  companyName: string;
  companyId: number | null;
  /** The CRM account's own name, which may differ from what was typed. */
  crmCompanyName: string | null;
  /** The CRM account manager -- `companies.owner_user_id`. */
  accountManagerUserId: number | null;
  accountManagerName: string | null;
  contactId: number | null;
  contactName: string | null;
  /** What was typed when this call was raised. Blank on most of them. */
  phone: string | null;
  /** The linked contact's own number, from the CRM. */
  contactPhone: string | null;
  /** The account's switchboard, from the CRM. */
  companyPhone: string | null;
  groupId: number | null;
  groupName: string | null;
  groupType: SalesCallGroupType | null;
  /** The group's order, which is what the worklist rings down. */
  groupPosition: number | null;
  status: SalesCallStatus;
  subject: string | null;
  details: string | null;
  /** A date somebody put on this call. Outranks every configured rule. */
  dueAt: string | null;
  assigneeUserId: number | null;
  assigneeName: string | null;
  assigneeAvatarUrl: string | null;
  claimedAt: string | null;
  requestedByUserId: number | null;
  createdByUserId: number | null;
  pinnedAt: string | null;
  pinnedByUserId: number | null;
  attemptCount: number;
  lastAttemptAt: string | null;
  callbackAt: string | null;
  scoreId: number | null;
  scoreLabel: string | null;
  scoreTone: SalesScoreTone | null;
  closingNotes: string | null;
  closedAt: string | null;
  closedByUserId: number | null;
  closedByName: string | null;
  createdAt: string;
  updatedAt: string;
  attention: Attention;
}

export interface SalesAttempt {
  id: number;
  outcome: SalesAttemptOutcome;
  note: string | null;
  callbackAt: string | null;
  occurredAt: string;
  userId: number | null;
  userName: string | null;
  userAvatarUrl: string | null;
}

export interface SalesEvent {
  id: number;
  type: string;
  data: Record<string, unknown> | null;
  createdAt: string;
  actorUserId: number | null;
  actorName: string | null;
}

export interface SalesCallDetail extends SalesCall {
  /** The linked contact's address, for a follow-up email. */
  contactEmail: string | null;
  /** The account's page in Team, when it is linked to Team. */
  teamUrl: string | null;
  attempts: SalesAttempt[];
  events: SalesEvent[];
}

export interface SalesCallPage {
  calls: SalesCall[];
  total: number;
  /** The pool exceeded the ranking ceiling; the order is of the oldest N. */
  truncated: boolean;
  mine: number;
}

export interface SalesGroup {
  id: number;
  name: string;
  type: SalesCallGroupType;
  /** Lower rings first. The desk's order, set in settings. */
  position: number;
  isActive: boolean;
  /** NULL means the group inherits the desk-wide rule. */
  overdueNoAttemptHours: number | null;
  overdueStaleDays: number | null;
  overdueDaysSinceRaised: number | null;
}

export interface SalesScore {
  id: number;
  label: string;
  tone: SalesScoreTone;
  position: number;
  isActive: boolean;
}

export interface OverdueRules {
  noAttemptHours: number;
  staleDays: number;
  /** NULL switches the third rule off, which is the default. */
  daysSinceRaised: number | null;
}

export interface SalesConfig {
  groups: SalesGroup[];
  scores: SalesScore[];
  rules: OverdueRules;
}

export interface SalesGroupCount {
  id: number | null;
  name: string;
  type: SalesCallGroupType | null;
  open: number;
  overdue: number;
  unclaimed: number;
}

export interface ClosedCallSummary {
  id: number;
  companyName: string;
  companyId: number | null;
  subject: string | null;
  closedAt: string | null;
  closedByUserId: number | null;
  closedByName: string | null;
  scoreLabel: string | null;
  scoreTone: SalesScoreTone | null;
  attemptCount: number;
  /** The closing note, for the preview line on the closed list. */
  closingNotes: string | null;
}

/** Not connected yet; the shape says so rather than showing a zero. */
export interface RevenuePlaceholder {
  available: false;
  windowDays: number;
}

export interface SalesBoard {
  /** The signed-in rep's own numbers, shown above the desk's. */
  me: {
    /** Calls where somebody picked up. Attempts that rang out are not counted. */
    answeredToday: number;
    /** All of yesterday, for the up/down against a part-finished today. */
    answeredYesterday: number;
    /**
     * Every attempt logged, whatever came of it.
     *
     * Shown beside the answered figure rather than instead of it. On its own,
     * "answered" reads as broken to a rep who has just logged two no-answers.
     */
    attemptsToday: number;
    attemptsYesterday: number;
    answeredThisWeek: number;
    /** The same elapsed slice of last week, for the week's up/down. */
    answeredLastWeek: number;
    assigned: number;
    revenue: RevenuePlaceholder;
  };
  kpi: {
    open: number;
    unclaimed: number;
    mine: number;
    overdue: number;
    answeredToday: number;
    answeredYesterday: number;
    /** Every attempt by anybody, answered or not. */
    attemptsToday: number;
    attemptsYesterday: number;
    answeredThisWeek: number;
    answeredLastWeek: number;
    closedToday: number;
    closedThisWeek: number;
    revenue: RevenuePlaceholder;
  };
  /**
   * Today's attempts per rep, per group.
   *
   * `groups` are only the ones actually rung today, so the table does not grow
   * a column per configured group. `byGroup` is keyed by group id as a string,
   * or "none" for calls raised before a group was required.
   */
  callsByPerson: {
    people: {
      userId: number;
      name: string;
      /** Every attempt logged today. */
      attempts: number;
      /** Answered attempts per group. */
      byGroup: Record<string, number>;
      /** Answered attempts, all groups. */
      total: number;
    }[];
    groups: { key: string; name: string }[];
  };
  attention: {
    total: number;
    neverAttempted: SalesCall[];
    stale: SalesCall[];
    pastDue: SalesCall[];
  };
  groups: SalesGroupCount[];
  closedRecently: ClosedCallSummary[];
  scoreSplit: {
    scoreId: number;
    label: string;
    tone: SalesScoreTone;
    total: number;
  }[];
}

/** One rep's standing workload. Right now, never inside a date range. */
export interface RepLoad {
  userId: number;
  name: string;
  assigned: number;
  /** Of those, overdue by their group's rules. */
  overdue: number;
}

/** One rep's activity within the dashboard's range. */
export interface RepActivity {
  userId: number;
  name: string;
  attempts: number;
  /** Of those attempts, the ones where somebody picked up. */
  answered: number;
  closed: number;
  won: number;
  lost: number;
  /**
   * The same figures for the equivalent slice of the period before -- this
   * week against the same elapsed part of last week, not against all of it.
   */
  previous: { attempts: number; answered: number; closed: number; won: number };
  /**
   * Wins over *decided* closes, or null when nothing was decided.
   *
   * Null rather than zero: a rep who closed nothing has no rate, where zero
   * means they closed things and won none. Rendering the first as 0% is an
   * accusation.
   */
  successRate: number | null;
}

export interface SalesReport {
  range: { from: string; to: string };
  /** The window the deltas are measured against. */
  comparedWith: { from: string; to: string };
  load: RepLoad[];
  activity: RepActivity[];
  scores: { scoreId: number; label: string; tone: string; total: number }[];
}

export interface CallListParams {
  status?: SalesCallStatus;
  groupId?: number;
  /** Calls filed under no group at all. */
  ungrouped?: boolean;
  groupType?: SalesCallGroupType;
  assigneeUserId?: number;
  unassigned?: boolean;
  /** Mine plus the unclaimed pool. Resolved server-side against the session. */
  mineOrPool?: boolean;
  overdueOnly?: boolean;
  q?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface CreateCallInput {
  companyName: string;
  companyId?: number | null;
  contactId?: number | null;
  phone?: string | null;
  groupId?: number | null;
  subject?: string | null;
  details?: string | null;
  dueAt?: string | null;
  assigneeUserId?: number | null;
}

/** What a quick attempt tag means in words. Used in every list and timeline. */
/**
 * The outcomes that mean somebody actually picked up.
 *
 * Mirrors `ANSWERED_ATTEMPT_OUTCOMES` in `@helpdesk/db`. The web does not
 * import the schema package -- it would drag mysql2 and drizzle into the
 * bundle for a pair of string literals -- which is why the outcome union above
 * is written out here too. Anything that drifts from the server drifts a label
 * away from the number underneath it.
 */
export const ANSWERED_OUTCOMES: readonly SalesAttemptOutcome[] = [
  "spoke",
  "callback_requested",
];

export const ATTEMPT_LABELS: Record<SalesAttemptOutcome, string> = {
  no_answer: "No answer",
  voicemail: "Left a voicemail",
  callback_requested: "Call back later",
  spoke: "Spoke to them",
  other: "Something else",
};

// ── The diary ───────────────────────────────────────────────────────────────

/** Whether somebody has to travel to it. */
export type SalesMeetingMode = "in_person" | "online";

/**
 * Two states, because there are only two things anybody needs to know: is it
 * still on, or was it called off. There is no "completed" -- a meeting that has
 * been and gone does not need declaring finished, and what happened is in the
 * notes.
 */
export type SalesMeetingStatus = "scheduled" | "cancelled";

/** Somebody from NetXL who is going. */
export interface MeetingAttendee {
  userId: number;
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
}

/**
 * Somebody outside NetXL who has been invited.
 *
 * An address, because the person you need in the room is regularly not in the
 * CRM. `contactId` and `name` are filled in when the address happens to match
 * a contact, so the list can say who they are -- never a requirement.
 */
export interface MeetingGuest {
  email: string;
  name: string | null;
  contactId: number | null;
  /** A contact on *this* meeting's company, rather than unknown or elsewhere. */
  onCompany: boolean;
}

/** Notes people write, tasks raised from the meeting, and the server's own. */
export type SalesMeetingNoteKind = "note" | "task" | "system";

/**
 * The three lists in the sidebar.
 *
 * `recent` is "no longer in the diary" rather than "in the past": a meeting
 * called off for next week has nothing to drive to, so it leaves `upcoming`
 * the moment it is cancelled. The server decides; see `meetings.ts` there.
 */
export type MeetingBucket = "today" | "upcoming" | "recent";

export interface SalesMeeting {
  id: number;
  companyName: string;
  companyId: number | null;
  crmCompanyName: string | null;
  accountManagerUserId: number | null;
  accountManagerName: string | null;
  contactId: number | null;
  contactName: string | null;
  contactPhone: string | null;
  /** The call it came out of, when it came out of one. */
  callId: number | null;
  /** The line that heads the event. */
  subject: string | null;
  mode: SalesMeetingMode;
  /** Where to go, for the ones you go to. */
  location: string | null;
  /** The link, for the ones you dial into. Kept apart so neither is lost. */
  meetingUrl: string | null;
  details: string | null;
  startAt: string;
  endAt: string | null;
  status: SalesMeetingStatus;
  /** Who from NetXL is going. Everybody on it sees the meeting as theirs. */
  attendees: MeetingAttendee[];
  /** Set when it is mirrored into somebody's Google calendar. */
  googleEventId: string | null;
  googleOrganiserUserId: number | null;
  cancelledAt: string | null;
  cancelledByUserId: number | null;
  cancelledByName: string | null;
  createdByUserId: number | null;
  createdAt: string;
  updatedAt: string;
  /**
   * How many things a person has written against it: the notes document
   * counts as one, beside any older log notes and tasks. `system` excluded.
   */
  noteCount: number;
  /** Follow-up emails sent from it. */
  followUpCount: number;
  /** Been and gone with nothing written against it. The diary's overdue. */
  needsNotes: boolean;
  /** Been and gone with no follow-up sent. */
  needsFollowUp: boolean;
  /** The start of the notes as plain text, for the Recent feed's grey line. */
  notesPreview: string | null;
  notesUpdatedAt: string | null;
  /** Guests not at one of our own domains. With no contact, zero is internal. */
  externalGuestCount: number;
}

/** Your own follow-up, saved as you write it and not sent yet. */
export interface MeetingFollowUpDraft {
  to: { email: string; name?: string }[];
  cc: { email: string; name?: string }[];
  subject: string;
  bodyHtml: string;
  updatedAt: string;
}

/** A follow-up email, as sent. */
export interface MeetingFollowUp {
  id: number;
  userId: number | null;
  userName: string | null;
  fromEmail: string;
  to: { email: string; name?: string }[];
  cc: { email: string; name?: string }[] | null;
  subject: string;
  bodyHtml: string;
  sentAt: string;
}

/** One of the last few meetings with the same company. */
export interface PreviousMeeting {
  id: number;
  subject: string | null;
  startAt: string;
  status: SalesMeetingStatus;
  notesText: string | null;
}

/** The Recent feed's filter chips. The server applies the same rules. */
export type MeetingOutcome = "needs_notes" | "needs_follow_up" | "done";

export interface SalesMeetingNote {
  id: number;
  kind: SalesMeetingNoteKind;
  body: string;
  /** Set only on a `task` entry. */
  taskExternalId: string | null;
  taskUrl: string | null;
  taskAssignee: string | null;
  taskDueAt: string | null;
  createdAt: string;
  userId: number | null;
  userName: string | null;
  userAvatarUrl: string | null;
}

export interface SalesMeetingDetail extends SalesMeeting {
  /**
   * Everybody invited from outside, oldest invitation first.
   *
   * Only on the detail read. A diary list wants to know who from here is going;
   * the guest list is what you check before the meeting, not while scanning
   * the week.
   */
  guests: MeetingGuest[];
  /** Newest first, all three kinds in one stream. */
  notes: SalesMeetingNote[];
  /** The notes document. Null when nothing is written. */
  notesHtml: string | null;
  notesUpdatedByName: string | null;
  followUps: MeetingFollowUp[];
  /** The signed-in person's own unsent draft, if they have one. */
  followUpDraft: MeetingFollowUpDraft | null;
  previous: PreviousMeeting[];
}

export interface SalesMeetingPage {
  meetings: SalesMeeting[];
  total: number;
  truncated: boolean;
}

/** What the sidebar badges, for the signed-in rep only. */
export interface MeetingCounts {
  today: number;
  upcoming: number;
  needsNotes: number;
}

export interface MeetingListParams {
  /** `past` is the Recent feed; `range` is a calendar window and needs from/to. */
  when?: MeetingBucket | "all" | "past" | "range";
  from?: string;
  to?: string;
  outcome?: MeetingOutcome;
  mine?: boolean;
  attendeeUserId?: number;
  companyId?: number;
  needsNotes?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface CreateMeetingInput {
  companyName: string;
  companyId?: number | null;
  contactId?: number | null;
  callId?: number | null;
  subject?: string | null;
  mode?: SalesMeetingMode;
  location?: string | null;
  meetingUrl?: string | null;
  details?: string | null;
  startAt: string;
  endAt?: string | null;
  /** Who from NetXL is going. Omitted means just whoever is booking it. */
  attendeeUserIds?: number[];
  /**
   * Anybody outside NetXL to invite, as email addresses. Replaces the whole
   * list on an update; adding to it is what emails somebody.
   */
  guests?: string[];
  /** Put it in your own Google calendar and invite everybody on it. */
  addToCalendar?: boolean;
}

/** How each state reads. The server writes the same words into the CRM. */
export const MEETING_STATUS_LABELS: Record<SalesMeetingStatus, string> = {
  scheduled: "In the diary",
  cancelled: "Cancelled",
};
